import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { getApiUrl } from '@/utils/apiConfig';

export interface BadgeInfo {
  id: string;
  name: string;
  description: string;
  icon: string;
  color: string;
}

export const BADGES_CATALOG: BadgeInfo[] = [
  { id: 'new_voter', name: 'Nouvel Électeur', description: 'A rejoint la plateforme et commencé à explorer.', icon: '🎓', color: 'bg-blue-500' },
  { id: 'fact_checker', name: 'Vérificateur', description: 'A vérifié au moins une information dans le Ring.', icon: '🕵️', color: 'bg-emerald-500' },
  { id: 'informed_citizen', name: 'Électeur Informé', description: 'S\'est intéressé aux programmes des partis politiques.', icon: '📖', color: 'bg-purple-500' },
  { id: 'quiz_master', name: 'Incollable', description: 'A obtenu un score parfait au quiz électoral.', icon: '🏆', color: 'bg-amber-500' },
  { id: 'engaged', name: 'Citoyen Engagé', description: 'A déposé un engagement ou une question.', icon: '🤝', color: 'bg-rose-500' },
];

interface GamificationState {
  xp: number;
  level: number;
  badges: string[];
  addXp: (amount: number, reason?: string) => void;
  unlockBadge: (badgeId: string) => void;
  hasBadge: (badgeId: string) => boolean;
  syncWithAuth: (metadataGamification: any) => void;
  pushToBackend: () => Promise<void>;
}

const calculateLevel = (xp: number) => {
  return Math.floor(Math.sqrt(xp / 100)) + 1;
};

export const useGamificationStore = create<GamificationState>()(
  persist(
    (set, get) => ({
      xp: 0,
      level: 1,
      badges: [],
      
      pushToBackend: async () => {
        if (typeof window === 'undefined') return;
        const userStr = localStorage.getItem('kaynin_user');
        const token = localStorage.getItem('kaynin_auth_token');
        if (!userStr || !token) return;
        
        try {
          const user = JSON.parse(userStr);
          if (user && user.id) {
            const state = get();
            const gamificationData = {
              xp: state.xp,
              level: state.level,
              badges: state.badges
            };
            await fetch(`${getApiUrl()}/auth.php?action=update_gamification`, {
              method: 'POST',
              headers: { 
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({ id: user.id, gamification: gamificationData })
            });
          }
        } catch (e) {
          console.error("Failed to sync gamification to backend", e);
        }
      },

      addXp: (amount: number, reason?: string) => {
        set((state) => {
          const newXp = state.xp + amount;
          const newLevel = calculateLevel(newXp);
          
          if (reason) {
            console.log(`+${amount} XP: ${reason}`);
          }
          
          // Déclencher un événement global pour l'UI (ex: Toast personnalisé ou animation)
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('kaynin_xp_gained', { detail: { amount, reason } }));
            if (newLevel > state.level) {
               window.dispatchEvent(new CustomEvent('kaynin_level_up', { detail: { level: newLevel } }));
            }
          }

          return { xp: newXp, level: newLevel };
        });
        get().pushToBackend();
      },

      unlockBadge: (badgeId: string) => {
        set((state) => {
          if (!state.badges.includes(badgeId)) {
            if (typeof window !== 'undefined') {
              window.dispatchEvent(new CustomEvent('kaynin_badge_unlocked', { detail: { badgeId } }));
            }
            return { badges: [...state.badges, badgeId] };
          }
          return state;
        });
        get().pushToBackend();
      },

      hasBadge: (badgeId: string) => {
        return get().badges.includes(badgeId);
      },

      syncWithAuth: (metadataGamification: any) => {
        if (metadataGamification) {
          set((state) => ({
            xp: Math.max(state.xp, metadataGamification.xp || 0),
            badges: Array.from(new Set([...state.badges, ...(metadataGamification.badges || [])])),
            level: calculateLevel(Math.max(state.xp, metadataGamification.xp || 0))
          }));
          get().pushToBackend();
        }
      }
    }),
    {
      name: 'kaynin_gamification',
    }
  )
);
